"use client";

/**
 * Shared lesson session logic: load newest lesson, generate, poll jobs,
 * audio transport, and self-rating. Used by TodaysLessonCard and the
 * /lessons page so playback + rating is not duplicated.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";

import {
  createLumiApiClient,
  type LessonDetail,
  type LessonSummary,
  type SelfRating,
} from "@/lib/lumi-api";
import {
  deriveLessonCardPhase,
  isGeneratingStatus,
  type LessonCardPhase,
} from "./lessonCardState";

const POLL_MS = 3500;

export interface UseLessonSessionResult {
  loading: boolean;
  lesson: LessonSummary | null;
  /** Full history for the language (newest first). */
  history: LessonSummary[];
  detail: LessonDetail | null;
  error: string | null;
  busy: boolean;
  phase: LessonCardPhase;
  justRated: boolean;
  generatingLocked: boolean;
  audioRef: RefObject<HTMLAudioElement | null>;
  audioUrl: string | null;
  playing: boolean;
  currentTime: number;
  duration: number;
  refreshLesson: () => Promise<LessonSummary | null>;
  handleGenerate: () => Promise<void>;
  handleRate: (rating: SelfRating) => Promise<void>;
  togglePlay: () => void;
  seek: (ratio: number) => void;
  finishForRating: () => void;
}

export function useLessonSession(language: string): UseLessonSessionResult {
  const api = useMemo(() => createLumiApiClient(), []);

  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<LessonSummary[]>([]);
  const [lesson, setLesson] = useState<LessonSummary | null>(null);
  const [detail, setDetail] = useState<LessonDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [awaitingRating, setAwaitingRating] = useState(false);
  const [justRated, setJustRated] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const detailIdRef = useRef<string | null>(null);

  const refreshLesson = useCallback(async () => {
    try {
      const list = await api.listLessons(language);
      const items = list.items ?? [];
      setHistory(items);
      const newest = items[0] ?? null;
      setLesson(newest);
      setError(null);

      if (newest && newest.status === "ready") {
        if (detailIdRef.current !== newest.id) {
          try {
            const d = await api.getLesson(newest.id);
            detailIdRef.current = d.id;
            setDetail(d);
          } catch {
            // Detail is optional for the summary card; keep list state.
          }
        }
      } else {
        detailIdRef.current = null;
        setDetail(null);
      }

      if (newest && !isGeneratingStatus(newest.status)) {
        setActiveJobId(null);
      }

      return newest;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load lessons");
      return null;
    } finally {
      setLoading(false);
    }
  }, [api, language]);

  useEffect(() => {
    setLoading(true);
    setAwaitingRating(false);
    setJustRated(false);
    setActiveJobId(null);
    setDetail(null);
    setHistory([]);
    void refreshLesson();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/language only
  }, [language]);

  const isGenerating =
    isGeneratingStatus(lesson?.status) || Boolean(activeJobId);

  useEffect(() => {
    if (!isGenerating) return;

    const id = setInterval(() => {
      void (async () => {
        if (activeJobId) {
          try {
            const job = await api.getJob(activeJobId);
            const st = (job.status ?? "").toLowerCase();
            if (st === "succeeded" || st === "failed" || st === "cancelled") {
              setActiveJobId(null);
            }
          } catch {
            // Keep polling via listLessons.
          }
        }
        await refreshLesson();
      })();
    }, POLL_MS);

    return () => clearInterval(id);
  }, [api, activeJobId, isGenerating, refreshLesson]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => setDuration(audio.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      setAwaitingRating(true);
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [detail?.audio_url]);

  useEffect(() => {
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setAwaitingRating(false);
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [lesson?.id]);

  const phase = deriveLessonCardPhase({
    loading,
    lesson,
    awaitingRating,
    justRated,
  });

  async function handleGenerate() {
    setBusy(true);
    setError(null);
    setJustRated(false);
    setAwaitingRating(false);
    try {
      const res = await api.generateLesson(language);
      setActiveJobId(res.job_id);
      setLesson((prev) => {
        if (prev && isGeneratingStatus(prev.status)) {
          return prev;
        }
        if (prev) {
          return { ...prev, status: "generating" };
        }
        return {
          id: res.job_id,
          seq: 1,
          title: "Generating…",
          status: "generating",
          duration_ms: null,
          new_count: null,
          review_count: null,
          self_rating: null,
          created_at: new Date().toISOString(),
        };
      });
      await refreshLesson();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not start lesson generation",
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleRate(rating: SelfRating) {
    if (!lesson) return;
    setBusy(true);
    setError(null);
    try {
      await api.postLessonResult(lesson.id, rating);
      setLesson({ ...lesson, self_rating: rating });
      setHistory((prev) =>
        prev.map((row) =>
          row.id === lesson.id ? { ...row, self_rating: rating } : row,
        ),
      );
      setAwaitingRating(false);
      setJustRated(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save rating");
    } finally {
      setBusy(false);
    }
  }

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      void audio.play().catch(() => {
        setError("Could not play audio");
      });
    } else {
      audio.pause();
    }
  }

  function seek(ratio: number) {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) {
      return;
    }
    const next = Math.max(0, Math.min(1, ratio)) * audio.duration;
    audio.currentTime = next;
    setCurrentTime(next);
  }

  function finishForRating() {
    audioRef.current?.pause();
    setAwaitingRating(true);
  }

  return {
    loading,
    lesson,
    history,
    detail,
    error,
    busy,
    phase,
    justRated,
    generatingLocked: isGenerating || busy,
    audioRef,
    audioUrl: detail?.audio_url ?? null,
    playing,
    currentTime,
    duration,
    refreshLesson,
    handleGenerate,
    handleRate,
    togglePlay,
    seek,
    finishForRating,
  };
}
